import {
  Box,
  chakra,
  Flex,
  FlexProps,
  HStack,
  SystemStyleObject,
  ThemeTypings,
  useColorModeValue,
  Wrap,
  WrapItem,
  WrapProps,
} from '@chakra-ui/react';
import { Badge } from '@main/complykit/components/badge';
import { Text } from '@main/complykit/components/text';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { LegendOrdinal } from '@visx/legend';
import { FormattedLabel } from '@visx/legend/lib/types';
import { withParentSize } from '@visx/responsive';
import {
  WithParentSizeProps,
  WithParentSizeProvidedProps,
} from '@visx/responsive/lib/enhancers/withParentSize';
import { scaleOrdinal } from '@visx/scale';
import { Pie } from '@visx/shape';
import { TooltipWithBounds, withTooltip } from '@visx/tooltip';
import { WithTooltipProvidedProps } from '@visx/tooltip/lib/enhancers/withTooltip';
import { createContext, ReactNode, useContext, useMemo } from 'react';

import { toneFromChartColor, useStatusTagChartFillColors } from '../status-tag/chart-colors';
import { ChartLegendSwatch } from './chart-legend';
import {
  ChartSegmentTooltipContent,
  PIE_CHART_TOOLTIP_OFFSET,
  useChartTooltipStyles,
} from './chart-segment-tooltip';
import {
  getPieChartCenterLabelFontSize,
  PIE_CHART_CENTER_LABEL_MAX_FONT_SIZE,
  PIE_CHART_CENTER_LABEL_MIN_FONT_SIZE,
} from './pie-center-label';
import {
  getPadAngle,
  getPieColor,
  getPieLabel,
  getPieValue,
  indexComparator,
  itemWithIndex,
} from './utils';

export interface PieDatum {
  label: string;
  value: number;
  color: ThemeTypings['colorSchemes'];
  key?: string;
}

interface PieChartProps {
  data: PieDatum[];
  halfMode?: boolean;
  donutThickness?: number;
  padAngle?: number;
  showSegmentTooltip?: boolean;
  formatSegmentTooltip?: (datum: PieDatum) => ReactNode;
  onSegmentClick?: (datum: PieDatum) => void;
  children: React.ReactNode;
}
const Context = createContext<PieChartProps | null>(null);

type PieChartMetrics = {
  halfMode: boolean;
  innerRadius: number;
};

const MetricsContext = createContext<PieChartMetrics | null>(null);

function usePieChartContext() {
  const context = useContext(Context);

  if (!context) {
    throw new Error('Graph and Legend components should be wrapped in <PieChart />');
  }

  return { context };
}

function usePieChartMetrics() {
  const metrics = useContext(MetricsContext);

  if (!metrics) {
    throw new Error('CenterLabel must be rendered inside <PieChart.Graph />');
  }

  return metrics;
}

function PieChartRoot(props: PieChartProps) {
  return <Context.Provider value={props}>{props.children}</Context.Provider>;
}

type GraphProps = { children?: ReactNode } & WithParentSizeProvidedProps &
  WithParentSizeProps &
  WithTooltipProvidedProps<PieDatum>;

export type PieChartGraphProps = { children?: ReactNode } & WithParentSizeProps;

function usePieChartConfig(props: Pick<GraphProps, 'parentWidth' | 'parentHeight'>) {
  const { context } = usePieChartContext();
  return useMemo(() => {
    const width = props.parentWidth ?? 0;
    const height = props.parentHeight ?? 0;

    const innerWidth = width;
    const innerHeight = height * (context.halfMode ? 2 : 1);

    return {
      width,
      height,
      innerWidth,
      innerHeight,
      radius: Math.min(innerWidth, innerHeight) / 2,
      centerY: innerHeight / 2,
      centerX: innerWidth / 2,
      donutThickness: context.donutThickness ?? 70,
      startAngle: context.halfMode ? Math.PI * 2.5 : Math.PI * 2,
      endAngle: context.halfMode ? Math.PI * 1.5 : 0,
    };
  }, [context.donutThickness, context.halfMode, props.parentHeight, props.parentWidth]);
}

type CenterLabelProps = {
  children: string | number;
  fontWeight?: number | string;
  minFontSize?: number;
  maxFontSize?: number;
};

function CenterLabel({
  children,
  fontWeight = 700,
  minFontSize = PIE_CHART_CENTER_LABEL_MIN_FONT_SIZE,
  maxFontSize = PIE_CHART_CENTER_LABEL_MAX_FONT_SIZE,
}: CenterLabelProps) {
  const { halfMode, innerRadius } = usePieChartMetrics();
  const label = String(children);
  const fontSize = useMemo(
    () =>
      getPieChartCenterLabelFontSize({
        innerRadius,
        label,
        minFontSize,
        maxFontSize,
        halfMode,
      }),
    [innerRadius, label, maxFontSize, minFontSize, halfMode],
  );

  return (
    <chakra.span fontWeight={fontWeight} fontSize={`${fontSize}px`} lineHeight="1em">
      {children}
    </chakra.span>
  );
}

function Graph({
  children,
  hideTooltip,
  showTooltip,
  tooltipData,
  tooltipLeft = 0,
  tooltipOpen,
  tooltipTop = 0,
  ...props
}: GraphProps) {
  const { context } = usePieChartContext();
  const config = usePieChartConfig(props);
  const resolvedTooltipStyles = useChartTooltipStyles();

  const svgStyles = useMemo<SystemStyleObject>(
    () => ({
      width: `${config.width}px`,
      height: `${config.height}px`,
      foreignObject: {
        width: '100%',
        height: '100%',
        x: '-50%',
        y: context.halfMode ? '-100%' : '-50%',
      },
    }),
    [config.width, config.height, context.halfMode],
  );
  const data = useMemo(
    () => context.data.map(itemWithIndex).filter((datum) => datum.value > 0),
    [context.data],
  );
  const arcFillColors = useStatusTagChartFillColors(data.map((datum) => datum.color));
  const arcFillColorByDatumIdx = useMemo(
    () => new Map(data.map((datum, index) => [datum.idx, arcFillColors[index] ?? ''])),
    [arcFillColors, data],
  );
  const showSegmentTooltip = context.showSegmentTooltip ?? false;
  const onSegmentClick = context.onSegmentClick;
  const segmentInteractive = showSegmentTooltip || !!onSegmentClick;
  const metrics = useMemo(
    () => ({
      halfMode: context.halfMode ?? false,
      innerRadius: config.radius - config.donutThickness,
    }),
    [config.donutThickness, config.radius, context.halfMode],
  );

  return (
    <>
      <chakra.svg sx={svgStyles}>
        <Group top={config.centerY} left={config.centerX}>
          <Pie
            data={data}
            pieValue={getPieValue}
            pieSort={indexComparator}
            outerRadius={config.radius}
            innerRadius={config.radius - config.donutThickness}
            cornerRadius={0}
            padAngle={context.padAngle ?? getPadAngle}
            startAngle={config.startAngle}
            endAngle={config.endAngle}
          >
            {(pie) =>
              pie.arcs.map((arc, index) => {
                const path = pie.path(arc);

                if (!path || arc.value <= 0) {
                  return null;
                }

                return (
                  <chakra.path
                    key={`${arc.data.label}-${index}`}
                    d={path}
                    fill={arcFillColorByDatumIdx.get(arc.data.idx) ?? 'currentColor'}
                    pointerEvents={segmentInteractive ? 'all' : 'none'}
                    cursor={segmentInteractive ? 'pointer' : undefined}
                    onMouseMove={(event) => {
                      if (!showSegmentTooltip) {
                        return;
                      }

                      const coords = localPoint(event);

                      showTooltip({
                        tooltipData: arc.data,
                        tooltipLeft: coords?.x ?? 0,
                        tooltipTop: coords?.y ?? 0,
                      });
                    }}
                    onMouseLeave={() => hideTooltip()}
                    onClick={() => onSegmentClick?.(arc.data)}
                  />
                );
              })
            }
          </Pie>
          <foreignObject pointerEvents="none">
            <MetricsContext.Provider value={metrics}>
              <Flex
                pointerEvents="none"
                height="full"
                justifyContent="center"
                alignItems={context.halfMode ? 'end' : 'center'}
                sx={
                  context.halfMode
                    ? {
                        '& > *': {
                          lineHeight: '1em',
                        },
                      }
                    : undefined
                }
              >
                {children}
              </Flex>
            </MetricsContext.Provider>
          </foreignObject>
        </Group>
      </chakra.svg>
      {showSegmentTooltip && tooltipOpen && tooltipData ? (
        <TooltipWithBounds
          left={tooltipLeft}
          top={tooltipTop}
          style={resolvedTooltipStyles}
          {...PIE_CHART_TOOLTIP_OFFSET}
        >
          {context.formatSegmentTooltip ? (
            context.formatSegmentTooltip(tooltipData)
          ) : (
            <ChartSegmentTooltipContent label={tooltipData.label} value={tooltipData.value} />
          )}
        </TooltipWithBounds>
      ) : null}
    </>
  );
}

type PieChartLegendVariant = 'values' | 'swatch';

type LegendProps = (WrapProps | FlexProps) & {
  render?: (labels: FormattedLabel<PieDatum, string>[]) => ReactNode;
  variant?: PieChartLegendVariant;
};
const Legend = ({ render, variant = 'values', ...props }: LegendProps) => {
  const { context } = usePieChartContext();

  const legendColorScale = useMemo(
    () =>
      scaleOrdinal({
        domain: context.data,
        range: context.data.map(getPieColor),
      }),
    [context.data],
  );

  return (
    <LegendOrdinal
      scale={legendColorScale}
      labelFormat={getPieLabel}
      children={
        render
          ? render
          : (labels) => {
              if (variant === 'swatch') {
                return (
                  <Wrap spacing={4} {...(props as WrapProps)}>
                    {labels.map((label) => (
                      <WrapItem key={label.index}>
                        <SwatchLegendShape label={label} />
                      </WrapItem>
                    ))}
                  </Wrap>
                );
              }

              return (
                <Flex gap={4} {...(props as FlexProps)}>
                  {labels.map((label) => (
                    <ValuesLegendShape key={label.index} label={label} />
                  ))}
                </Flex>
              );
            }
      }
    />
  );
};

function ValuesLegendShape({ label }: { label: FormattedLabel<PieDatum, string> }) {
  /* A count chip is a Badge, on the same tone the slice fill resolves from,
   * so the chip and its slice cannot disagree. */
  return (
    <Flex gap={1} alignItems="center">
      <Badge tone={toneFromChartColor(label.datum.color)}>{label.datum.value}</Badge>
      <Box width="max-content">
        <Text size="xs">{label.text}</Text>
      </Box>
    </Flex>
  );
}

function SwatchLegendShape({ label }: { label: FormattedLabel<PieDatum, string> }) {
  const [fillColor] = useStatusTagChartFillColors([label.datum.color]);

  return (
    <HStack spacing={2}>
      <ChartLegendSwatch color={fillColor ?? ''} />
      <Text size="sm" color="muted">
        {label.text}
      </Text>
    </HStack>
  );
}

type PieChartComponent = {
  (props: PieChartProps): ReactNode;
  CenterLabel: typeof CenterLabel;
  Graph: (props: PieChartGraphProps) => ReactNode;
  Legend: typeof Legend;
};

const PieChartWithSubcomponents = PieChartRoot as PieChartComponent;
PieChartWithSubcomponents.CenterLabel = CenterLabel;
PieChartWithSubcomponents.Graph = withParentSize(
  withTooltip(Graph),
) as unknown as PieChartComponent['Graph'];
PieChartWithSubcomponents.Legend = Legend;

export const PieChart = PieChartWithSubcomponents;
